import * as React from 'react';
import { Popover } from 'radix-ui';
import cx from 'classnames';
import { usePopup } from './popup.context';
import { Surface, useBrandConfig, type Surfaces } from '@dx-ui/global-ui-prism';
import { useRouter } from 'next/router';
import { isRtl } from '@dx-ui/utilities-get-language-direction';

export type PopupContentPropsBase = {
  /** Controls the X alignment of the pop up content to button */
  align?: Popover.PopoverContentProps['align'];
  /** Add override styling to the pop up */
  popupStyles?: React.CSSProperties;
  /** Controls what side of the trigger button should the pop up show */
  side?: Popover.PopoverContentProps['side'];
  /** Supports surfaceType based on prism design */
  surfaceType?: Surfaces;
} & React.ComponentProps<'div'>;

const PopupContent = ({
  align = 'start',
  children,
  className,
  style,
  popupStyles = {},
  side = 'bottom',
  surfaceType = 'base',
  ...rest
}: PopupContentPropsBase) => {
  const { popupId, setOpen, triggerRef } = usePopup();
  const brandConfig = useBrandConfig();
  const isSurfaceEnabled = brandConfig.surfaceConfig.isEnabled;
  const { locale = 'en' } = useRouter();
  const isRTL = isRtl(locale);

  return (
    <Popover.Portal>
      <Surface type={surfaceType}>
        <Popover.Content
          id={popupId}
          data-testid="popup-content"
          side={side}
          align={isRTL ? 'end' : align}
          sideOffset={4}
          onEscapeKeyDown={() => setOpen(false)}
          onPointerDownOutside={(e) => {
            if (triggerRef.current?.contains(e.target as Node)) {
              e.preventDefault();
              return;
            }
            setOpen(false);
          }}
          className={cx(
            'z-[60] max-w-[100vw] rounded border border-solid p-2 shadow-lg',
            {
              'border-surface bg-surface': isSurfaceEnabled,
              'border-border bg-bg': !isSurfaceEnabled,
            },
            className
          )}
          style={{ ...style, ...popupStyles }}
          {...rest}
        >
          {children}
        </Popover.Content>
      </Surface>
    </Popover.Portal>
  );
};

export { PopupContent };
export default PopupContent;
